import React, { useState, useEffect } from 'react';
import { Search, Filter, CheckCircle, XCircle, RefreshCw, DollarSign, Clock, AlertTriangle, User, ArrowUpDown } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  waiting: { bg: 'var(--warning-bg)', color: 'var(--warning)', label: 'Waiting' },
  finished: { bg: 'var(--success-bg)', color: 'var(--success)', label: 'Finished' },
  expired: { bg: 'var(--bg-tertiary)', color: 'var(--text-tertiary)', label: 'Expired' },
  failed: { bg: 'var(--error-bg)', color: 'var(--error)', label: 'Failed' },
  review_required: { bg: 'var(--error-bg)', color: 'var(--error)', label: 'Review Required' },
  refunded: { bg: 'var(--info-bg)', color: 'var(--info)', label: 'Refunded' },
};

export default function AdminCryptoPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ status: '', user_id: '', plan: '' });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedPayment, setExpandedPayment] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const limit = 20;

  useEffect(() => { loadPayments(); }, [page, filters, sortBy, sortOrder]);

  const loadPayments = async () => {
    setLoading(true);
    try {
      const params = { page, limit, sort_by: sortBy, sort_order: sortOrder };
      if (filters.status) params.status = filters.status;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.plan) params.plan = filters.plan;
      const res = await api.get('/api/admin/crypto-payments', { params });
      setPayments(res.data.payments || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (paymentId) => {
    setActionLoading(paymentId);
    try {
      await api.post(`/api/admin/crypto-payments/${paymentId}/verify`);
      toast.success('Payment verified and user upgraded');
      loadPayments();
    } catch {
      toast.error('Failed to verify payment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRefund = async (paymentId, amount = null) => {
    if (!window.confirm(`Refund ${amount ? '$' + amount : 'full amount'}?`)) return;
    setActionLoading(paymentId);
    try {
      await api.post(`/api/admin/crypto-payments/${paymentId}/refund`, { amount });
      toast.success('Refund initiated');
      loadPayments();
    } catch {
      toast.error('Failed to refund');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && payments.length === 0) {
    return <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 24px)', maxWidth: 1400 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: '1.25rem', fontWeight: 700, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <DollarSign size={22} color="var(--accent-primary)" /> Crypto Payments
          </h2>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>Manage NOWPayments transactions, verify, and process refunds</p>
        </div>
        <button onClick={loadPayments} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 8,
          border: '1px solid var(--info)40', background: 'transparent',
          color: 'var(--info)', cursor: 'pointer', fontSize: 12, fontWeight: 600,
        }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filters.status} onChange={e => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
          style={filterStyle}>
          <option value="">All Statuses</option>
          <option value="waiting">Waiting</option>
          <option value="finished">Finished</option>
          <option value="expired">Expired</option>
          <option value="failed">Failed</option>
          <option value="review_required">Review Required</option>
          <option value="refunded">Refunded</option>
        </select>
        <select value={filters.plan} onChange={e => { setFilters({ ...filters, plan: e.target.value }); setPage(1); }}
          style={filterStyle}>
          <option value="">All Plans</option>
          <option value="free">Free</option>
          <option value="starter">Starter</option>
          <option value="growth">Growth</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <input
          type="text"
          placeholder="Search by User ID..."
          value={filters.user_id}
          onChange={e => { setFilters({ ...filters, user_id: e.target.value }); setPage(1); }}
          style={{ ...filterStyle, width: 160 }}
        />
        <button onClick={() => { setFilters({ ...filters, status: 'review_required' }); setPage(1); }}
          style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid var(--error)40',
            background: filters.status === 'review_required' ? 'var(--error-bg)' : 'transparent',
            color: 'var(--error)', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
          <AlertTriangle size={12} /> Review Queue
        </button>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)', marginLeft: 'auto' }}>
          {total} total payments
        </span>
      </div>

      {/* Table */}
      <div style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
        borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--shadow-sm)',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-primary)', background: 'var(--bg-tertiary)' }}>
                <th style={thStyle} onClick={() => toggleSort('payment_id')} className="sortable">
                  Payment ID <ArrowUpDown size={10} />
                </th>
                <th style={thStyle} onClick={() => toggleSort('user_id')} className="sortable">
                  User <ArrowUpDown size={10} />
                </th>
                <th style={thStyle}>Plan</th>
                <th style={thStyle} onClick={() => toggleSort('amount_usd')} className="sortable">
                  Amount <ArrowUpDown size={10} />
                </th>
                <th style={thStyle}>Currency</th>
                <th style={thStyle} onClick={() => toggleSort('payment_status')} className="sortable">
                  Status <ArrowUpDown size={10} />
                </th>
                <th style={thStyle}>Risk</th>
                <th style={thStyle} onClick={() => toggleSort('created_at')} className="sortable">
                  Date <ArrowUpDown size={10} />
                </th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p, i) => {
                const statusStyle = STATUS_COLORS[p.payment_status] || STATUS_COLORS.waiting;
                const isExpanded = expandedPayment === p.payment_id;
                const riskLevel = (p.risk_score || 0) >= 50 ? 'var(--error)' : (p.risk_score || 0) >= 20 ? 'var(--warning)' : 'var(--text-tertiary)';
                return (
                  <React.Fragment key={p.payment_id}>
                    <tr
                      style={{ borderBottom: '1px solid var(--border-primary)', cursor: 'pointer', background: i % 2 === 0 ? 'transparent' : 'var(--bg-tertiary)' }}
                      onClick={() => setExpandedPayment(isExpanded ? null : p.payment_id)}
                    >
                      <td style={{ ...tdStyle, fontSize: 11, fontFamily: 'monospace', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.payment_id?.length > 20 ? p.payment_id.slice(0, 17) + '...' : p.payment_id}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <User size={12} color="var(--text-tertiary)" />
                          <span>{p.user_email || `User #${p.user_id}`}</span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 600, textTransform: 'capitalize' }}>{p.plan}</td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--success)' }}>${p.amount_usd?.toFixed(2) || '0.00'}</td>
                      <td style={{ ...tdStyle, fontSize: 11 }}>{p.pay_currency?.toUpperCase() || ', '}</td>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: statusStyle.bg, color: statusStyle.color,
                          border: `1px solid ${statusStyle.color}30`,
                        }}>
                          {statusStyle.label}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ color: riskLevel, fontWeight: 600, fontSize: 12 }}>
                          {p.risk_score || 0}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 11, color: 'var(--text-tertiary)' }}>
                        {p.created_at ? new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ', '}
                      </td>
                      <td style={tdStyle} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {p.payment_status === 'waiting' && (
                            <button onClick={() => handleVerify(p.payment_id)} disabled={actionLoading === p.payment_id}
                              style={actionBtnStyle('var(--success)')} title="Verify & Upgrade">
                              {actionLoading === p.payment_id ? <RefreshCw size={12} className="spin" /> : <CheckCircle size={12} />}
                            </button>
                          )}
                          {p.payment_status === 'finished' && (
                            <button onClick={() => {
                              const amount = prompt('Enter refund amount in USD (leave blank for full):', p.amount_usd || '');
                              handleRefund(p.payment_id, amount || null);
                            }} disabled={actionLoading === p.payment_id}
                              style={actionBtnStyle('var(--error)')} title="Refund">
                              <XCircle size={12} />
                            </button>
                          )}
                          {p.payment_status === 'review_required' && (
                            <>
                              <button onClick={() => handleVerify(p.payment_id)} disabled={actionLoading === p.payment_id}
                                style={actionBtnStyle('var(--success)')} title="Approve">
                                {actionLoading === p.payment_id ? <RefreshCw size={12} className="spin" /> : <CheckCircle size={12} />}
                              </button>
                              <button onClick={async () => {
                                if (!window.confirm('Reject this payment?')) return;
                                setActionLoading(p.payment_id);
                                try { await api.post(`/api/admin/crypto-payments/${p.payment_id}/reject`); toast.success('Rejected'); loadPayments(); }
                                catch { toast.error('Failed'); }
                                finally { setActionLoading(null); }
                              }} style={actionBtnStyle('var(--error)')} title="Reject">
                                <XCircle size={12} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded detail row */}
                    {isExpanded && (
                      <tr style={{ background: 'var(--bg-primary)' }}>
                        <td colSpan={9} style={{ padding: '16px 20px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                            <div>
                              <div style={detailLabel}>Pay Address</div>
                              <div style={{ ...detailValue, fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{p.pay_address || ', '}</div>
                            </div>
                            <div>
                              <div style={detailLabel}>Pay Amount</div>
                              <div style={detailValue}>{p.pay_amount || ', '} {p.pay_currency?.toUpperCase()}</div>
                            </div>
                            <div>
                              <div style={detailLabel}>Actually Paid</div>
                              <div style={detailValue}>{p.actually_paid ? `$${p.actually_paid.toFixed(2)}` : ', '}</div>
                            </div>
                            <div>
                              <div style={detailLabel}>Transaction Hash</div>
                              <div style={{ ...detailValue, fontFamily: 'monospace', fontSize: 10, wordBreak: 'break-all' }}>{p.txid || ', '}</div>
                            </div>
                            <div>
                              <div style={detailLabel}>Billing Cycle</div>
                              <div style={{ ...detailValue, textTransform: 'capitalize' }}>{p.billing_cycle || ', '}</div>
                            </div>
                            <div>
                              <div style={detailLabel}>Expires At</div>
                              <div style={detailValue}>{p.expires_at ? new Date(p.expires_at).toLocaleString() : ', '}</div>
                            </div>
                            <div>
                              <div style={detailLabel}>IPN Received</div>
                              <div style={detailValue}>{p.ipn_received_at ? new Date(p.ipn_received_at).toLocaleString() : ', '}</div>
                            </div>
                            <div>
                              <div style={detailLabel}>IPN Status</div>
                              <div style={detailValue}>{p.ipn_status || ', '}</div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {payments.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                    No crypto payments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
            style={pageBtnStyle(page === 1)}>← Prev</button>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Page {page} of {totalPages}
          </span>
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
            style={pageBtnStyle(page >= totalPages)}>Next →</button>
        </div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────

const filterStyle = {
  padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-primary)',
  background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 12, fontWeight: 500,
};

const thStyle = {
  textAlign: 'left', padding: '12px 14px', fontSize: 11, fontWeight: 600,
  color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em',
  cursor: 'pointer', userSelect: 'none',
};

const tdStyle = { padding: '12px 14px', fontSize: 13, color: 'var(--text-primary)' };

const actionBtnStyle = (color) => ({
  padding: '4px 8px', borderRadius: 6, border: `1px solid ${color}40`,
  background: 'transparent', color, cursor: 'pointer', display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center',
});

const detailLabel = { fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 };
const detailValue = { fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 };

const pageBtnStyle = (disabled) => ({
  padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border-primary)',
  background: 'transparent', color: disabled ? 'var(--text-tertiary)' : 'var(--text-secondary)',
  cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600,
  opacity: disabled ? 0.5 : 1,
});
